import { useState, useEffect, useMemo } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
// Font size persistence helpers
const FONT_SIZE_KEY = 'scribelate_transcript_font_size';
const DEFAULT_FONT_SIZE = 16;

function getStoredFontSize() {
  const stored = localStorage.getItem(FONT_SIZE_KEY);
  const parsed = stored ? parseInt(stored, 10) : NaN;
  return !isNaN(parsed) && parsed > 8 && parsed < 48 ? parsed : DEFAULT_FONT_SIZE;
}

function setStoredFontSize(size: number) {
  localStorage.setItem(FONT_SIZE_KEY, String(size));
}
import {
  Box,
  TextField,
  Button,
  Stack,
  Typography,
  Paper,
  IconButton,
  CircularProgress,
  Grid,
  Popover,
  Tooltip,
  Collapse,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  AutoAwesome as AutoIcon,
  BookmarkAdd as BookmarkAddIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Translate as TranslateIcon,
  StickyNote2 as NoteIcon,
  StickyNote2Outlined as NoteOutlinedIcon,
  Spellcheck as SpellcheckIcon,
} from '@mui/icons-material';
import { NoteModal } from '../notes/NoteModal';
import { useSaveTranscript, useAutoTranscribe } from '../../hooks/useTranscripts';
import { useAddVocabularyWord, useLinkExistingVocabulary, useVocabularyBySegment } from '../../hooks/useVocabulary';
import { useTranslateText } from '../../hooks/useTranslation';
import { useProjectById } from '../../hooks/useProjects';
import type { Segment, VocabularyWord } from '../../types/api.types';
import toast from 'react-hot-toast';
import { showCelebrationToast } from '../../utils/celebrations';
import * as DOMPurify from 'dompurify';
import { decodeHtmlEntities } from '../../utils/textUtils';
import { HighlightedText } from '../vocabulary/HighlightedText';
import { useAutoSave } from '../../hooks/useAutoSave';
import { SaveStatus } from '../common/SaveStatus';
import { useExplainSegment } from '../../hooks/useExplainer';
import { ExplanationPanel } from './ExplanationPanel';


interface TranscriptEditorProps {
  segment: Segment;
  projectId: number;
  episodeId: number;
  /**
   * Fired when the user starts working with this segment's transcript (clicking
   * into the edit surface or focusing the textarea). Parents use it to pin the
   * segment so playback stops at its end — mirrors the YouTube player's
   * onSegmentWorked boundary lock.
   */
  onSegmentWorked?: () => void;
  /** Fired when the user leaves edit mode — releases the segment boundary lock. */
  onExitEditMode?: () => void;
}

export const TranscriptEditor = ({ segment, projectId, episodeId, onSegmentWorked, onExitEditMode }: TranscriptEditorProps) => {
  // Font size state (persisted)
  const [fontSize, setFontSize] = useState<number>(getStoredFontSize());
  const [isEditMode, setIsEditMode] = useState(false);

  const handleFontSizeChange = (delta: number) => {
    setFontSize(prev => {
      const next = Math.max(10, Math.min(40, prev + delta));
      setStoredFontSize(next);
      return next;
    });
  };
  // Get the most recent manual transcript (highest ID = most recent)
  const getLatestManualTranscript = () => {
    const manualTranscripts = segment.transcripts?.filter(t => t.source === 'manual') || [];
    if (manualTranscripts.length === 0) return '';
    const latest = manualTranscripts.reduce((prev, current) => 
      (current.id > prev.id) ? current : prev
    );
    return latest.transcription || '';
  };
  
  const [manualText, setManualText] = useState(getLatestManualTranscript());
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [selectionAnchor, setSelectionAnchor] = useState<{ top: number; left: number } | null>(null);
  const [manualTranslation, setManualTranslation] = useState('');

  const { t } = useTranslation('player');
  const { t: tSegments } = useTranslation('segments');

  const {
    isOpen: explainOpen,
    isLoading: explainLoading,
    explanation,
    error: explainError,
    explain,
    dismiss: dismissExplain,
    resetForSegment,
  } = useExplainSegment(segment.id);

  // Reset explanation panel when navigating to a different segment
  React.useEffect(() => {
    resetForSegment();
  }, [segment.id]); // resetForSegment is stable (useCallback with no deps)

  const { mutate: saveTranscript } = useSaveTranscript();
  const { mutate: autoTranscribe, isPending: isTranscribing } = useAutoTranscribe();
  const { mutate: addVocabularyWord, isPending: isAddingWord } = useAddVocabularyWord();
  const { mutate: linkExistingVocabulary, isPending: isMarkingVocab } = useLinkExistingVocabulary();
  const translateText = useTranslateText();
  
  // Fetch project to get language codes
  const { data: project } = useProjectById(projectId);

  // Fetch vocabulary words for this segment
  const { data: vocabularyData } = useVocabularyBySegment(segment.id);
  const segmentVocabWords = useMemo<VocabularyWord[]>(() => vocabularyData ?? [], [vocabularyData]);

  const autoTranscript = segment.transcripts?.find(t => t.source === 'Google');

  // DOMPurify module shape can vary depending on bundler/tsconfig; normalize to a callable purifier
  const purifier: { sanitize: (s: string, opt?: any) => string } = ((DOMPurify as any)?.default ?? DOMPurify) as any;

  // Load manual text only when segment changes (not when content changes)
  // This prevents refetched data from overwriting unsaved user edits
  useEffect(() => {
    const manualTranscript = getLatestManualTranscript();
    setManualText(manualTranscript);
  }, [segment.id]);

  const handleSave = () => {
    saveTranscript({
      segmentId: segment.id,
      name: `Segment ${segment.id}`,
      transcription: manualText,
      source: 'manual',
    }, {
      onSuccess: () => {
        showCelebrationToast('TRANSCRIPT_SAVED', 'Transcript saved!', '✨');
      },
    });
    // Keep isDirty as true to prevent refetched data from overwriting our save
    // It will reset when user navigates to a new segment
  };

  // Auto-save functionality
  const { status: saveStatus, lastSavedAt, triggerSave } = useAutoSave({
    onSave: handleSave,
    value: manualText,
    initialValue: getLatestManualTranscript(),
    delay: 5000,
    enabled: true,
  });

  const handleAutoTranscribe = () => {
    autoTranscribe({
      id: segment.id,
      projectId,
      episodeId,
      startTime: segment.startTime,
      endTime: segment.endTime,
    });
  };

  const handleCopyAuto = () => {
    if (autoTranscript?.transcription) {
      const raw = decodeHtmlEntities(autoTranscript.transcription);
      // If annotated ruby HTML, strip tags for clipboard
      const plain = raw.replace(/<[^>]*>/g, '');
      navigator.clipboard.writeText(plain);
      toast.success('Copied to clipboard');
    }
  };

  const handleTextSelect = (e: React.MouseEvent<HTMLDivElement>) => {
    // window.getSelection() works for read-mode divs but NOT for textarea elements.
    // For textarea (edit mode), fall back to selectionStart/selectionEnd on the target.
    let text = window.getSelection()?.toString().trim() ?? '';

    if (!text) {
      const target = e.target as HTMLTextAreaElement;
      if (typeof target.selectionStart === 'number' && typeof target.selectionEnd === 'number') {
        text = (target.value ?? '').substring(target.selectionStart, target.selectionEnd).trim();
      }
    }

    if (text && text.length > 0 && text.length <= 100) {
      setSelectedText(text);
      // Use mouse coordinates so the popover appears right at the cursor in both modes
      setSelectionAnchor({ top: e.clientY + 8, left: e.clientX });
      setManualTranslation(''); // Clear previous translation
    } else {
      setSelectedText('');
      setSelectionAnchor(null);
      setManualTranslation('');
    }
  };

  const handleAddToVocabulary = () => {
    if (!selectedText || !manualText) return;

    addVocabularyWord({
      word: selectedText,
      context: manualText, // Full transcript as context
      segmentId: segment.id,
      projectId,
      sourceType: 'transcript',
      translation: manualTranslation || undefined,
    }, {
      onSuccess: () => {
        toast.success('Word added to vocabulary!');
        handleClosePopover();
      },
    });
  };

  const handleMarkVocabulary = () => {
    if (!manualText.trim()) return;
    linkExistingVocabulary(
      { segmentId: segment.id, projectId, sourceType: 'transcript' },
      {
        onSuccess: (result) => {
          if (result.linked > 0) {
            toast.success(`Marked ${result.linked} vocabulary word${result.linked === 1 ? '' : 's'}`);
          } else if (result.matched > 0) {
            toast.success('Your vocabulary words here are already marked');
          } else {
            toast('No saved vocabulary words found in this transcript');
          }
        },
      },
    );
  };

  const handleTranslate = () => {
    if (!selectedText || !project) return;

    // Map language names to ISO codes for MyMemory API
    const languageToCode: Record<string, string> = {
      'japanese': 'ja',
      'english': 'en',
      'spanish': 'es',
      'french': 'fr',
      'german': 'de',
      'italian': 'it',
      'portuguese': 'pt',
      'chinese': 'zh',
      'korean': 'ko',
      'russian': 'ru',
      'turkish': 'tr',
    };
    
    const getLanguageCode = (lang: string) => {
      const lower = lang.toLowerCase();
      // If it already looks like a code (2-3 chars or has hyphen), extract the ISO code
      if (lower.includes('-')) {
        return lower.split('-')[0];
      }
      // Otherwise map the full name to code
      return languageToCode[lower] || lower;
    };
    
    const sourceLanguage = getLanguageCode(project.projectLanguage);
    const targetLanguage = getLanguageCode(project.userLanguage);

    console.log('Translating:', { selectedText, sourceLanguage, targetLanguage, projectLanguage: project.projectLanguage, userLanguage: project.userLanguage });

    translateText.mutate({
      text: selectedText,
      sourceLanguage,
      targetLanguage,
    }, {
      onSuccess: (data) => {
        console.log('Translation success:', data);
        // Decode HTML entities like &#39; to '
        setManualTranslation(decodeHtmlEntities(data.translatedText));
      },
      onError: (error) => {
        console.error('Translation error:', error);
        toast.error('Translation failed');
      },
    });
  };

  const handleClosePopover = () => {
    setSelectedText('');
    setSelectionAnchor(null);
    setManualTranslation('');
    window.getSelection()?.removeAllRanges();
  };

  return (
    <>
    <Paper sx={{ p: 2, height: '100%', bgcolor: 'background.default' }} variant="outlined">
      <Grid container spacing={2} sx={{ height: '100%', width: '100%' }}>
        {/* Manual Transcription */}
        <Grid size={{ xs: 12, md: 6 }} sx={{ minWidth: 0, '@media (min-width: 900px)': { flex: '1 1 0' } }}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                Your Transcription
              </Typography>
              <Tooltip title={isEditMode ? 'Switch to view mode' : 'Switch to edit mode'}>
                <IconButton
                  size="small"
                  onClick={() => {
                    if (isEditMode) {
                      setIsEditMode(false);
                      onExitEditMode?.();
                    } else {
                      setIsEditMode(true);
                      onSegmentWorked?.();
                    }
                  }}
                  color={isEditMode ? 'primary' : 'default'}
                  data-testid="transcript-edit-mode-toggle"
                >
                  {isEditMode ? <VisibilityIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Button size="small" onClick={() => handleFontSizeChange(2)} sx={{ minWidth: 0, px: 1 }} title="Increase font size">＋</Button>
              <Button size="small" onClick={() => handleFontSizeChange(-2)} sx={{ minWidth: 0, px: 1 }} title="Decrease font size">－</Button>
              <Tooltip title="Mark my vocabulary words in this transcript">
                <span>
                  <IconButton
                    size="small"
                    onClick={handleMarkVocabulary}
                    disabled={isMarkingVocab || !manualText.trim()}
                    data-testid="mark-vocabulary-btn"
                  >
                    {isMarkingVocab ? <CircularProgress size={16} /> : <SpellcheckIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={segment.hasTranscriptNote ? tSegments('noteViewEditTooltip') : tSegments('noteAddTooltip')}>
                <IconButton
                  size="small"
                  onClick={() => setIsNoteModalOpen(true)}
                  color={segment.hasTranscriptNote ? 'primary' : 'default'}
                  data-testid="transcript-note-icon"
                >
                  {segment.hasTranscriptNote ? <NoteIcon fontSize="small" /> : <NoteOutlinedIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Stack>

            {isEditMode ? (
              <Box 
                onMouseUp={handleTextSelect}
                sx={{ position: 'relative' }}
                data-testid="transcript-selectable"
              >
                <TextField
                  multiline
                  minRows={4}
                  fullWidth
                  placeholder="Type what you hear in the audio..."
                  value={manualText}
                  onChange={(e) => setManualText(e.target.value)}
                  onFocus={() => onSegmentWorked?.()}
                  sx={{
                    mb: 1,
                    fontSize: fontSize,
                    '& .MuiInputBase-root': {
                      minHeight: '150px',
                      maxHeight: '300px',
                      overflow: 'auto',
                      alignItems: 'flex-start',
                    },
                    '& .MuiInputBase-input': { fontSize: fontSize, lineHeight: 1.6 },
                    bgcolor: 'background.paper',
                  }}
                />
              </Box>
            ) : (
              <Paper
                sx={{
                  p: 2,
                  bgcolor: 'background.paper',
                  minHeight: '150px',
                  maxHeight: '300px',
                  overflow: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  cursor: 'text',
                }}
                onClick={() => {
                  setIsEditMode(true);
                  onSegmentWorked?.();
                }}
                onMouseUp={handleTextSelect}
                data-testid="transcript-selectable"
              >
                {manualText ? (
                  <div style={{ fontSize: fontSize, lineHeight: 1.6 }}>
                    <HighlightedText
                      text={manualText}
                      vocabularyWords={segmentVocabWords}
                      segmentId={segment.id}
                    />
                  </div>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Click to start typing...
                  </Typography>
                )}
              </Paper>
            )}
            
            {/* Word Selection Popover */}
            <Popover
              open={!!selectedText && !!selectionAnchor}
              anchorReference="anchorPosition"
              anchorPosition={selectionAnchor ?? undefined}
              onClose={handleClosePopover}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              data-testid="vocab-save-popover"
            >
              <Box sx={{ p: 2, minWidth: 220, maxWidth: 420, width: 'max-content' }}>
                <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }} data-testid="vocab-selected-word">
                  {selectedText}
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  placeholder={t('vocabTranslationPlaceholder')}
                  value={manualTranslation}
                  onChange={(e) => setManualTranslation(e.target.value)}
                  sx={{ mb: 1 }}
                  data-testid="vocab-translation-input"
                />
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CopyIcon />}
                    onClick={() => {
                      navigator.clipboard.writeText(selectedText).catch(() => {});
                      toast.success('Copied to clipboard');
                      handleClosePopover();
                    }}
                    data-testid="vocab-copy-btn"
                  >
                    {t('copyWord')}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={translateText.isPending ? <CircularProgress size={16} /> : <TranslateIcon />}
                    onClick={handleTranslate}
                    disabled={translateText.isPending || !project}
                    data-testid="vocab-translate-btn"
                    sx={{ display: 'grid' }}
                  >
                    {/* Both labels occupy the same grid cell; the longer one (invisible when
                        inactive) reserves the button's width so it doesn't resize on click. */}
                    <Box
                      component="span"
                      sx={{ gridArea: '1 / 1', visibility: translateText.isPending ? 'hidden' : 'visible' }}
                    >
                      {t('translateWord')}
                    </Box>
                    <Box
                      component="span"
                      sx={{ gridArea: '1 / 1', visibility: translateText.isPending ? 'visible' : 'hidden' }}
                    >
                      {t('translatingWord')}
                    </Box>
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={isAddingWord ? <CircularProgress size={16} /> : <BookmarkAddIcon />}
                    onClick={handleAddToVocabulary}
                    disabled={isAddingWord}
                    data-testid="vocab-save-btn"
                  >
                    {t('saveVocab')}
                  </Button>
                </Stack>
              </Box>
            </Popover>
            <Stack direction="row" alignItems="center" sx={{ width: '100%', mt: 1 }}>
              <Button
                variant="contained"
                onClick={triggerSave}
                disabled={
                  !manualText.trim() ||
                  manualText === getLatestManualTranscript()
                }
                size="small"
                sx={{ mr: 1 }}
              >
                {saveStatus === 'saving' ? 'Saving...' : 'Save'}
              </Button>
              <SaveStatus status={saveStatus} lastSavedAt={lastSavedAt} />
              <Box sx={{ flexGrow: 1 }} />
              <Button
                size="small"
                variant="outlined"
                onClick={() => explain(manualText)}
                disabled={explainLoading}
              >
                {explanation !== null && explainOpen ? 'Regenerate' : 'Explain'}
              </Button>
            </Stack>
            {/* AI Explanation Panel (D-02) — collapsed by default, opens on Explain click */}
            <Collapse in={explainOpen} unmountOnExit={false}>
              <ExplanationPanel
                isLoading={explainLoading}
                explanation={explanation}
                error={explainError}
                onDismiss={dismissExplain}
              />
            </Collapse>
          </Box>
        </Grid>

        {/* Auto Transcription */}
        <Grid size={{ xs: 12, md: 6 }} sx={{ minWidth: 0, '@media (min-width: 900px)': { flex: '1 1 0' } }}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                Auto Transcription
              </Typography>
              <Button size="small" onClick={() => handleFontSizeChange(2)} sx={{ minWidth: 0, px: 1 }} title="Increase font size">＋</Button>
              <Button size="small" onClick={() => handleFontSizeChange(-2)} sx={{ minWidth: 0, px: 1 }} title="Decrease font size">－</Button>
              {autoTranscript && (
                <IconButton size="small" onClick={handleCopyAuto} title="Copy to clipboard">
                  <CopyIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>

            {!autoTranscript && (
              <Button
                variant="outlined"
                startIcon={isTranscribing ? <CircularProgress size={16} /> : <AutoIcon />}
                onClick={handleAutoTranscribe}
                disabled={isTranscribing}
                size="small"
              >
                {isTranscribing ? 'Transcribing...' : 'Generate Auto-Transcription'}
              </Button>
            )}


            {autoTranscript && (
              <Paper
                sx={{
                  p: 2,
                  bgcolor: 'action.hover',
                  minHeight: '150px',
                  maxHeight: '300px',
                  overflow: 'auto',
                }}
                variant="outlined"
              >
                {autoTranscript.alt_transcription ? (
                  autoTranscript.alt_transcription.includes('<ruby') ? (
                    <div
                      style={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: fontSize, lineHeight: 1.6 }}
                      dangerouslySetInnerHTML={{ __html: purifier.sanitize(decodeHtmlEntities(autoTranscript.alt_transcription), {ALLOWED_TAGS: ['ruby','rt','rb','br','b','i','em','strong','span','div','p'], ALLOWED_ATTR: ['class']}) }}
                    />
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: fontSize, lineHeight: 1.6 }}
                    >
                      {decodeHtmlEntities(autoTranscript.alt_transcription)}
                    </Typography>
                  )
                ) : autoTranscript.transcription ? (
                  autoTranscript.transcription.includes('<ruby') ? (
                    <div
                      style={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: fontSize, lineHeight: 1.6 }}
                      dangerouslySetInnerHTML={{ __html: purifier.sanitize(decodeHtmlEntities(autoTranscript.transcription), {ALLOWED_TAGS: ['ruby','rt','rb','br','b','i','em','strong','span','div','p'], ALLOWED_ATTR: ['class']}) }}
                    />
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: fontSize, lineHeight: 1.6 }}
                    >
                      {decodeHtmlEntities(autoTranscript.transcription)}
                    </Typography>
                  )
                ) : (
                  <Typography variant="body2">No transcription available</Typography>
                )}
              </Paper>
            )}

          </Box>
        </Grid>
      </Grid>
    </Paper>
    <NoteModal
      open={isNoteModalOpen}
      onClose={() => setIsNoteModalOpen(false)}
      segmentId={segment.id}
      noteType="transcript"
      sourceText={manualText}
      hasExistingNote={segment.hasTranscriptNote}
    />
    </>
  );
};
